import { AxiosInstance } from 'axios';

import { DMReportItem, IngredientUsageReportItem, Report } from '../../../store/entities/reports/reports.types';
import { APIManagerConfig } from '../apiManager';
import { PresetSettings } from '../preset/preset.types';
import { InventoryReportType, ReportType } from '../../../store/entities/reports/reports.constants';
import {
  getDateFormat,
  getDecimalSeparator,
  getThousandSeparator,
  getTimeFormat,
  saveFile,
} from '../../helpers/utils/utils';
import { DataTransferMethods } from '../../../store/application/loadMixers/loadMixers.types';

const ReportsServiceFactory = ({ HTTPBase, config }: { HTTPBase: AxiosInstance; config: APIManagerConfig }) => {
  const userSettings = {
    dateFormat: getDateFormat(),
    timeFormat: getTimeFormat(),
    decimalSeparator: getDecimalSeparator(),
    thousandSeparator: getThousandSeparator(),
  };
  return {
    downloadReport: (
      reportType: string,
      format: string,
      ext: string,
      daysBack: number,
      endDate: Date,
      fileName: string,
    ): Promise<void> => {
      return HTTPBase.post(
        `farm/${config.farmId}/reports/${reportType}/report`,
        {
          preset: {
            dataSettings: {
              daysBack,
              endDate,
            },
          },
          userSettings,
        },
        {
          headers: { Accept: format },
          responseType: 'blob',
        },
      ).then((res) => saveFile(res, format, ext, fileName));
    },

    downloadReportByPreset: (
      reportType: string,
      format: string,
      ext: string,
      preset: PresetSettings,
      fileName: string,
    ): Promise<void> => {
      return HTTPBase.post(
        `farm/${config.farmId}/reports/${reportType}/report`,
        {
          preset,
          userSettings,
        },
        {
          headers: { Accept: format },
          responseType: 'blob',
        },
      ).then((res) => saveFile(res, format, ext, fileName));
    },
    downloadListReport: (
      format: string,
      ext: string,
      entity: string,
      fileName?: string,
      parameters?: any,
    ): Promise<void> => {
      const entityMap = {
        ingredients: {
          endpoint: 'ingredients',
          params: {
            filter: 'nopremix',
            hideNotInUse: String(parameters.hideNotInUse),
          },
        },
        premixes: {
          endpoint: 'rations',
          params: {
            filter: 'premix',
            hideNotInUse: String(parameters.hideNotInUse),
          },
        },
        recipes: {
          endpoint: 'rations',
          params: {
            filter: 'nopremix',
            hideNotInUse: String(parameters.hideNotInUse),
          },
        },
        groups: {
          endpoint: 'groups',
          params: {
            filter: 'np',
            hideNotInUse: String(parameters.hideNotInUse),
          },
        },
        feedingsLoads: {
          endpoint: 'feedings/loads',
          params: {
            periods: parameters.periods || [],
            mixers: parameters.mixers || [],
            channel: 'usb', // download only file for now
            loadNo: 0, // generate all loads for now
          },
        },
        feedingsList: {
          endpoint: 'feedings/loaddata',
          params: {
            periods: parameters.periods || [],
            mixers: parameters.mixers || [],
            channel: 'usb', // download only file for now
            loadNo: 0, // generate all loads for now
          },
        },
        lots: {
          endpoint: 'lots',
          params: {
            neb: parameters.hideClosed ? 'now' : undefined,
          },
        },
      };

      const params = entityMap[entity] && entityMap[entity].params ? entityMap[entity].params : undefined;

      const endpoint = entityMap[entity] ? entityMap[entity].endpoint : entity;

      return HTTPBase.post(
        `farm/${config.farmId}/${endpoint}/report`,
        {
          userSettings,
          preset: {
            dataSettings: params,
          },
        },
        {
          headers: { Accept: format },
          responseType: 'blob',
        },
      ).then((res) => saveFile(res, format, ext, fileName));
    },
    previewLoadsReport: (entity: 'feedingList' | 'feedingsLoads', parameters?: any) => {
      const endpointMap = { feedingsList: 'feedings/loaddata', feedingsLoads: 'feedings/loads' };
      const endpoint = endpointMap[entity];

      return HTTPBase.post(
        `farm/${config.farmId}/${endpoint}/report`,
        {
          userSettings,
          preset: {
            dataSettings: {
              endpoint,
              periods: parameters.periods || [],
              mixers: parameters.mixers || [],
              effectiveDate: parameters.effectiveDate,
              channel: parameters.channel || DataTransferMethods.USB,
              loadNo: 0, // generate all loads for now
            },
          },
        },
        {
          headers: { Accept: 'application/pdf' },
          responseType: 'blob',
        },
      ).then((response) => {
        const file = new Blob([response.data], { type: 'application/pdf' });
        return URL.createObjectURL(file);
      });
    },
    getReportData: (
      reportType: string,
      endDate: Date,
      daysBack: number,
    ): Promise<Report<DMReportItem> | Report<IngredientUsageReportItem>> =>
      HTTPBase.get(`farm/${config.farmId}/reports/${reportType}/data`, {
        params: {
          daysBack,
          endDate,
        },
      }).then((res) => res.data),
    getReportDataByPreset: (
      reportType: string,
      preset: PresetSettings,
    ): Promise<Report<DMReportItem> | Report<IngredientUsageReportItem>> =>
      HTTPBase.post(`farm/${config.farmId}/reports/${reportType}/data`, {
        preset,
        userSettings,
      }).then((res) => res.data),
    getReportFilterData: (
      reportType: ReportType | InventoryReportType,
      includeDeleted: boolean,
      hideNotInUse: boolean,
    ): Promise<Report<DMReportItem> | Report<IngredientUsageReportItem>> =>
      HTTPBase.get(
        `farm/${config.farmId}/reports/${reportType}/filterData?includeDeleted=${includeDeleted}&hideNotInUse=${hideNotInUse}`,
      ).then((res) => res.data),
  };
};

export default ReportsServiceFactory;
